import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private tokenKey = 'auth_token';
  private claimsKey = 'auth_claims';
  private refreshTokenKey = 'auth_refresh_token';
  private refreshUrl = environment.END_POINT_SERVICES + '/auth/refresh';

  constructor(
    private jwtHelper: JwtHelperService,
    private http: HttpClient) { }

  // Guardar el token (y opcionalmente el refresh token) en localStorage
  saveToken(token: any, refreshToken?: string): void {
    localStorage.setItem(this.tokenKey, token);
    // Decodificar y guardar los claims
    const claims = this.jwtHelper.decodeToken(token);
    if (claims) {
      localStorage.setItem(this.claimsKey, JSON.stringify(claims));
    }
    if (refreshToken) {
      this.saveRefreshToken(refreshToken);
    }
  }

  // Guardar el refresh token
  saveRefreshToken(refreshToken: string): void {
    localStorage.setItem(this.refreshTokenKey, refreshToken);
  }

  // Obtener el token
  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  // Obtener el refresh token
  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  // Eliminar el token, los claims y el refresh token al cerrar sesión
  removeToken(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.claimsKey);
    localStorage.removeItem(this.refreshTokenKey);
  }

  // Obtener los claims (payload) del token
  getTokenClaims(): any {
    const token = this.getToken();
    if (token && !this.jwtHelper.isTokenExpired(token)) {
      return this.jwtHelper.decodeToken(token);
    }
    return null;
  }

  // Comprobar si el usuario está autenticado
  isAuthenticated(): boolean {
    const token = this.getToken();
    return token ? !this.jwtHelper.isTokenExpired(token) : false;
  }

  // Comprobar si el token está próximo a expirar (e.g., faltan menos de 5 minutos)
  isTokenNearExpiration(): boolean {
    const token = this.getToken();
    if (token) {
      const expirationDate = this.jwtHelper.getTokenExpirationDate(token);
      if (expirationDate) {
        const currentTime = new Date().getTime();
        const timeRemaining = expirationDate.getTime() - currentTime;
        return timeRemaining < 5 * 60 * 1000; // Menos de 5 minutos
      }
    }
    return false;
  }

  // Renovar el token usando el refresh token contra la API
  refreshToken(): Observable<string> {
    const body = {
      token: this.getToken(),
      refreshToken: this.getRefreshToken()
    };
    return this.http.post<any>(this.refreshUrl, body).pipe(
      map(response => {
        const newToken = response?.body?.token;
        const newRefreshToken = response?.body?.refreshToken;
        if (!newToken) {
          throw new Error('No se pudo renovar el token');
        }
        this.saveToken(newToken, newRefreshToken);
        return newToken;
      })
    );
  }
}
